import { Alignment, Button, MenuItem } from "@blueprintjs/core";
import { ItemRenderer, Select } from "@blueprintjs/select";
import { segmentTrackScopeChanged } from "analytics/analyticTrackEvents";
import useMapViewConfiguration from "contexts/MapViewConfigurationContext/hooks/useMapViewConfiguration";
import { useMapViewRoutingMetadata } from "contexts/RoutingMetadataContext";
import React, { useMemo } from "react";
import { getScopeFromScopes, Scope, ScopeName } from "types/scope";
import FilterWrapper from "./FilterWrapper";

type ScopeSelectFilterProps = {
    readonly inSidebar: boolean;
};

const ScopeSelectFilter: React.FC<ScopeSelectFilterProps> = (props) => {
    const {
        busFiltersConfiguration: { busFilters, updateScope, filtersDisabled }
    } = useMapViewConfiguration();
    const { scope } = busFilters;
    const { acceptableScopes, componentConfig } = useMapViewRoutingMetadata();

    const orderedScopes = useMemo(
        () =>
            getOrderedScopesForScopeDropdown(
                acceptableScopes,
                componentConfig.prioritizedScopeNames
            ),
        [acceptableScopes, componentConfig.prioritizedScopeNames]
    );

    const selectedScopeConfig = getScopeFromScopes(orderedScopes, scope);

    return (
        <FilterWrapper
            label="Scope"
            limitInputMaxWidth={props.inSidebar}
            limitLabelMaxWidth={props.inSidebar}
        >
            <Select
                items={[...orderedScopes]}
                itemRenderer={getSelectOptionItemForConfig}
                onItemSelect={(newScope: Scope) => {
                    updateScope(newScope.scope);
                    segmentTrackScopeChanged(newScope.scope);
                }}
                activeItem={selectedScopeConfig}
                filterable={false}
                popoverProps={{ minimal: true }}
                fill={true}
            >
                <Button
                    // selectedScopeConfig should never be undefined. If it is, we've set something up wrong.
                    text={selectedScopeConfig?.display_name ?? "Unknown scope"}
                    rightIcon="caret-down"
                    small={true}
                    fill={true}
                    alignText={Alignment.LEFT}
                    ellipsizeText={true}
                    disabled={filtersDisabled}
                />
            </Select>
        </FilterWrapper>
    );
};

const getOrderedScopesForScopeDropdown = (
    acceptableScopes: ReadonlyArray<Scope>,
    prioritizedScopeNames: ReadonlyArray<ScopeName> | undefined
): ReadonlyArray<Scope> => {
    const prioritizedScopeNamesWithFallback = prioritizedScopeNames ?? [];
    if (prioritizedScopeNamesWithFallback.length === 0) {
        return acceptableScopes;
    }

    const priorityByScope = new Map(
        prioritizedScopeNamesWithFallback.map((scopeName, index) => [
            scopeName,
            index
        ])
    );
    const unlistedPriority = prioritizedScopeNamesWithFallback.length;

    return [...acceptableScopes].sort((a, b) => {
        const aPriority = priorityByScope.get(a.scope) ?? unlistedPriority;
        const bPriority = priorityByScope.get(b.scope) ?? unlistedPriority;
        return (
            aPriority - bPriority ||
            a.display_name.localeCompare(b.display_name)
        );
    });
};

const getSelectOptionItemForConfig: ItemRenderer<Scope> = (
    scopeConfig: Scope,
    { handleClick, modifiers }
) => (
    <MenuItem
        active={modifiers.active}
        key={scopeConfig.scope}
        text={scopeConfig.display_name}
        onClick={handleClick}
    />
);

export default ScopeSelectFilter;
